import {isFilled} from '@prismicio/client'
import {PrismicNextImage} from '@prismicio/next/pages'
import {PrismicRichText} from '@prismicio/react'
import cn from 'classnames'
import {useLinkResolver} from 'prismicio'
import type {ButtonDocument, CtaV2SliceFullWidthImage} from 'prismicio-types'
// eslint-disable-next-line boundaries/dependencies -- grandfathered cross-slice import, predates the slice-boundaries rule
import {RatingsSection} from 'slices/HeroV2/RatingsSection'
import {ButtonV2} from 'src/common/ButtonV2'
import {Container} from 'src/common/Container'
import {getButtonsData} from 'src/common/helpers'

type FullWidthVariationProps = {
  slice: CtaV2SliceFullWidthImage
  pageUid: string
  componentIndex: number
  buttonsData?: ButtonDocument[]
}

export const FullWidthVariation = ({slice, buttonsData, pageUid, componentIndex}: FullWidthVariationProps) => {
  const {linkResolver} = useLinkResolver()

  const {primaryButtonData, secondaryButtonData} = getButtonsData({
    buttonsData,
    primaryButton: slice.primary.primary_button,
    secondaryButton: slice.primary.secondary_button,
  })

  return (
    <Container data-slice-variation={slice.variation} className="px-5 max-lg:py-10 md:px-10 lg:px-5 lg:pb-20 xl:px-0">
      <div className="relative isolate mx-auto flex h-[400px] w-full max-w-[1200px] flex-col justify-between overflow-hidden rounded-lg p-6 md:h-[553px] md:p-10">
        <PrismicNextImage
          field={slice.primary.image}
          className={cn('absolute inset-0 size-full object-cover max-lg:hidden', {
            'max-lg:hidden': isFilled.image(slice.primary.mobile_image),
          })}
          priority
          quality={100}
          alt=""
        />
        <PrismicNextImage
          field={slice.primary.mobile_image}
          className="absolute inset-0 size-full object-cover lg:hidden"
          priority
          quality={100}
          alt=""
        />
        {isFilled.group(slice.primary.ratings) && (
          <RatingsSection
            ratings={slice.primary.ratings}
            type="Animated"
            centered={false}
            version="v2"
            centeredMobile={false}
          />
        )}
        <div className="z-10 flex w-full flex-col gap-8">
          <div>
            <PrismicRichText
              field={slice.primary.title}
              components={{
                heading2: ({children}) => (
                  <h2 className="md:text-tg-h2 lg:text-tg-h1 text-[32px] font-normal leading-[34px] text-moss-white max-md:max-w-[90%]">
                    {children}
                  </h2>
                ),
                em: ({children}) => (
                  <>
                    <br className="max-md:hidden" />
                    <span>{children}</span>
                  </>
                ),
              }}
            />
          </div>
          <div className="flex flex-col gap-3 md:flex-row md:gap-4">
            {primaryButtonData ? (
              <ButtonV2
                variant="primary"
                href={linkResolver(primaryButtonData.link, 'primary-button-cta')}
                trackingData={{
                  mktgId: primaryButtonData.marketing_id || '',
                  dataType: primaryButtonData.intent || '',
                  location: `component-${componentIndex}-cta-v3`,
                  pageName: pageUid,
                }}
                className="w-fit text-[12px] font-semibold uppercase"
                size="medium"
                id={`component-${componentIndex}-cta-1`}
                border={false}
                newTrackingProps={{
                  interaction: {
                    element: 'primary-button',
                    elementText: primaryButtonData?.label || '',
                    location: `component-${componentIndex}-cta-v3`,
                  },
                  pageType: pageUid,
                }}
              >
                {primaryButtonData.label}
              </ButtonV2>
            ) : null}
            {secondaryButtonData ? (
              <ButtonV2
                variant="secondary"
                href={linkResolver(secondaryButtonData.link, 'secondary-button-cta')}
                trackingData={{
                  mktgId: secondaryButtonData.marketing_id || '',
                  dataType: secondaryButtonData.intent || '',
                  location: `component-${componentIndex}-cta-v3`,
                  pageName: pageUid,
                }}
                className="w-fit text-[12px] font-semibold uppercase"
                size="medium"
                id={`component-${componentIndex}-cta-2`}
                border={false}
                newTrackingProps={{
                  interaction: {
                    element: 'secondary-button',
                    elementText: secondaryButtonData?.label || '',
                    location: `component-${componentIndex}-cta-v3`,
                  },
                  pageType: pageUid,
                }}
              >
                {secondaryButtonData.label}
              </ButtonV2>
            ) : null}
          </div>
        </div>
      </div>
    </Container>
  )
}
